import styled from "styled-components";
import { ChangeEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import Text from "@/components/Text";
import {
  InputGroup,
  ResetButton,
  ResetHolder,
  StyledInput,
  Wrapper,
} from "@/pages/AssignTopics/components/Filter";
import { selectTeamSettings } from "@/selectors";
import { Dispatch, RootState } from "@/utils/store";
import useDebounce from "@/hooks/useDebounce";
import useDidUpdate from "@/hooks/useDidUpdate";
import {
  ensureStringContainsDecimalNumbersOnly,
  ensureStringContainsPositiveDigitsOnly,
} from "@/utils/helpers";

const Wrap = styled.div`
  display: flex;
  margin: 0 0 24px;
  flex-wrap: wrap;
`;

const StyledInputGroup = styled(InputGroup)`
  margin: 0;
  width: 100%;
`;

const FilterRow = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
  margin-top: 16px;
  &:first-child {
    margin-top: 8px;
  }
`;

interface State {
  minVideos: string;
  minViews: string;
  minDuration: string;
  maxDuration: string;
  minAge: string;
  minV7Index: string;
}

const TopScriptTopicsFilter = () => {
  const dispatch = useDispatch<Dispatch>();

  const { teamId } = useParams();

  const teamSettings = useSelector((rootState: RootState) =>
    selectTeamSettings(rootState, teamId!)
  );

  const [values, setValues] = useState<State>({
    minVideos: teamSettings?.top_script_topics_min_videos ?? "",
    minViews: teamSettings?.top_script_topics_min_views ?? "",
    minDuration: teamSettings?.top_script_topics_min_duration ?? "",
    maxDuration: teamSettings?.top_script_topics_max_duration ?? "",
    minAge: teamSettings?.top_script_topics_min_age ?? "",
    minV7Index: teamSettings?.top_script_topics_min_v7_index ?? "",
  });

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name !== "minV7Index" && !ensureStringContainsPositiveDigitsOnly(value))
      return;

    if (name === "minV7Index" && !ensureStringContainsDecimalNumbersOnly(value))
      return;

    setValues((prev) => ({ ...prev, [name as keyof State]: value }));
  };

  const debouncedValues = useDebounce(values, 500);

  const resetValue = (key: keyof State) =>
    setValues((prev) => ({ ...prev, [key]: "" }));

  useDidUpdate(() => {
    dispatch.teams.updateSettings({
      teamId: teamId!,
      updatedSettings: {
        top_script_topics_min_videos: debouncedValues.minVideos,
        top_script_topics_min_views: debouncedValues.minViews,
        top_script_topics_min_duration: debouncedValues.minDuration,
        top_script_topics_max_duration: debouncedValues.maxDuration,
        top_script_topics_min_age: debouncedValues.minAge,
        top_script_topics_min_v7_index: debouncedValues.minV7Index,
      },
    });
  }, [debouncedValues, teamId]);

  return (
    <Wrap>
      <FilterRow>
        <StyledInputGroup>
          <Text $type="smallHeadline">Minimum videos</Text>
          <Wrapper>
            <ResetHolder>
              <ResetButton onClick={() => resetValue("minVideos")}>
                Reset
              </ResetButton>
            </ResetHolder>
            <StyledInput
              placeholder="Enter number"
              name="minVideos"
              value={values.minVideos}
              onChange={onChange}
            />
          </Wrapper>
        </StyledInputGroup>
        <StyledInputGroup>
          <Text $type="smallHeadline">Minimum duration (sec)</Text>
          <Wrapper>
            <ResetHolder>
              <ResetButton onClick={() => resetValue("minDuration")}>
                Reset
              </ResetButton>
            </ResetHolder>
            <StyledInput
              placeholder="Enter number"
              name="minDuration"
              value={values.minDuration}
              onChange={onChange}
            />
          </Wrapper>
        </StyledInputGroup>
        <StyledInputGroup>
          <Text $type="smallHeadline">Maximum duration (sec)</Text>
          <Wrapper>
            <ResetHolder>
              <ResetButton onClick={() => resetValue("maxDuration")}>
                Reset
              </ResetButton>
            </ResetHolder>
            <StyledInput
              placeholder="Enter number"
              name="maxDuration"
              value={values.maxDuration}
              onChange={onChange}
            />
          </Wrapper>
        </StyledInputGroup>
      </FilterRow>
      <FilterRow>
        <StyledInputGroup>
          <Text $type="smallHeadline">Minimum views</Text>
          <Wrapper>
            <ResetHolder>
              <ResetButton onClick={() => resetValue("minViews")}>
                Reset
              </ResetButton>
            </ResetHolder>
            <StyledInput
              placeholder="Enter number"
              name="minViews"
              value={values.minViews}
              onChange={onChange}
            />
          </Wrapper>
        </StyledInputGroup>
        <StyledInputGroup>
          <Text $type="smallHeadline">Minimum age (days)</Text>
          <Wrapper>
            <ResetHolder>
              <ResetButton onClick={() => resetValue("minAge")}>
                Reset
              </ResetButton>
            </ResetHolder>
            <StyledInput
              placeholder="Enter number"
              name="minAge"
              value={values.minAge}
              onChange={onChange}
            />
          </Wrapper>
        </StyledInputGroup>
        <StyledInputGroup>
          <Text $type="smallHeadline">Minimum V7 Index (%)</Text>
          <Wrapper>
            <ResetHolder>
              <ResetButton onClick={() => resetValue("minV7Index")}>
                Reset
              </ResetButton>
            </ResetHolder>
            <StyledInput
              placeholder="Enter number"
              name="minV7Index"
              value={values.minV7Index}
              onChange={onChange}
            />
          </Wrapper>
        </StyledInputGroup>
      </FilterRow>
    </Wrap>
  );
};

export default TopScriptTopicsFilter;
